import type { IdMapsByCollection } from './types';

const FIELDS_TO_OMIT_FROM_CLONE = new Set(['id', 'createdAt', 'updatedAt', '_status']);

/**
 * Recursively clones an object and remaps relationship/upload IDs using the given maps.
 * Sets `tenant` to destinationTenantId and omits id/createdAt/updatedAt so Payload generates new ones.
 * For relationship/upload fields: string IDs are replaced by the new ID from the appropriate map;
 * if the target collection is excluded (e.g. users), the value is set to null.
 */
export const remapDocumentForDuplicate = <T extends Record<string, unknown>>(
  doc: T,
  idMapsByCollection: IdMapsByCollection,
  destinationTenantId: string,
  excludedCollectionSlugs: Set<string>,
  onMissingRelation?: (sourceId: string, relationTo: string | null) => void,
): T => {
  if (doc === null || typeof doc !== 'object') {
    return doc;
  }

  if (Array.isArray(doc)) {
    return doc.map((item) =>
      remapDocumentForDuplicate(
        item as Record<string, unknown>,
        idMapsByCollection,
        destinationTenantId,
        excludedCollectionSlugs,
        onMissingRelation,
      ),
    ) as unknown as T;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(doc)) {
    if (FIELDS_TO_OMIT_FROM_CLONE.has(key)) {
      continue;
    }

    if (key === 'tenant') {
      result[key] = destinationTenantId;
      continue;
    }

    const remapped = remapValue(value, idMapsByCollection, destinationTenantId, excludedCollectionSlugs, onMissingRelation);
    result[key] = remapped;
  }

  return result as T;
};

const isPopulatedUploadDocument = (value: Record<string, unknown>): boolean =>
  typeof value.id === 'string' && typeof value.mimeType === 'string' && ('filename' in value || 'cloudinary' in value);

const remapUploadId = (sourceId: string, idMapsByCollection: IdMapsByCollection): string | null => {
  const newId = idMapsByCollection.media?.get(sourceId);
  return newId ?? null;
};

const remapValue = (
  value: unknown,
  idMapsByCollection: IdMapsByCollection,
  destinationTenantId: string,
  excludedCollectionSlugs: Set<string>,
  onMissingRelation?: (sourceId: string, relationTo: string | null) => void,
): unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  if (isRelationObject(value)) {
    const relationTo = value.relationTo ?? null;
    const id = String(value.id);
    if (relationTo && excludedCollectionSlugs.has(relationTo)) {
      return null;
    }
    if (relationTo) {
      const newId = idMapsByCollection[relationTo]?.get(id);
      if (newId !== undefined) {
        return { id: newId, relationTo };
      }
      onMissingRelation?.(id, relationTo);
      return null;
    }
    for (const map of Object.values(idMapsByCollection)) {
      const newId = map.get(id);
      if (newId !== undefined) {
        return newId;
      }
    }
    onMissingRelation?.(id, null);
    return null;
  }

  if (typeof value === 'string') {
    for (const map of Object.values(idMapsByCollection)) {
      const newId = map.get(value);
      if (newId !== undefined) {
        return newId;
      }
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => remapValue(item, idMapsByCollection, destinationTenantId, excludedCollectionSlugs, onMissingRelation));
  }

  if (typeof value === 'object' && value !== null) {
    const objectValue = value as Record<string, unknown>;
    if (isPopulatedUploadDocument(objectValue)) {
      const remappedId = remapUploadId(String(objectValue.id), idMapsByCollection);
      if (remappedId === null) onMissingRelation?.(String(objectValue.id), 'media');
      return remappedId;
    }
    return remapDocumentForDuplicate(
      value as Record<string, unknown>,
      idMapsByCollection,
      destinationTenantId,
      excludedCollectionSlugs,
      onMissingRelation,
    );
  }

  return value;
};

/**
 * Payload relation refs are plain { id: string } or { id: string, relationTo: string } with no other keys.
 * Array items (e.g. redirections with origin, target, id) must not be treated as relations.
 */
export const isRelationObject = (value: unknown): value is { id: string; relationTo?: string } => {
  if (typeof value !== 'object' || value === null) return false;
  const o = value as Record<string, unknown>;
  if (!('id' in o) || typeof o.id !== 'string') return false;
  const keys = Object.keys(o);
  return keys.length <= 2 && keys.every((k) => k === 'id' || k === 'relationTo');
};
